import React, { useRef, useCallback } from 'react';
import {
  Percent,
  ArrowRightLeft,
  ClipboardCheck,
  Shield,
  Droplets,
  Zap,
  Briefcase,
  ChevronRight,
} from 'lucide-react';
import { PILLARS } from '../../config/pillars';

const ICON_MAP = {
  Percent,
  ArrowRightLeft,
  ClipboardCheck,
  Shield,
  Droplets,
  Zap,
  Briefcase,
};

/**
 * StackSelector — 7 numbered steps rendered as an ARIA tablist.
 * Desktop: flex layout with chevron arrows between tabs.
 * Mobile: grid-cols-2 sm:grid-cols-3 (arrows hidden).
 * Active border: #3832e3.  Keyboard: Left/Right arrows navigate tabs.
 */
const StackSelector = ({ selected, onSelect }) => {
  const tabRefs = useRef([]);

  const handleKeyDown = useCallback(
    (e) => {
      const idx = PILLARS.findIndex((p) => p.key === selected);
      let next = idx;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        next = (idx + 1) % PILLARS.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        next = (idx - 1 + PILLARS.length) % PILLARS.length;
      } else if (e.key === 'Home') {
        e.preventDefault();
        next = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        next = PILLARS.length - 1;
      } else {
        return;
      }

      onSelect(PILLARS[next].key);
      tabRefs.current[next]?.focus();
    },
    [selected, onSelect],
  );

  return (
    <div role="tablist" aria-label="Service stack">
      {/* Mobile: grid layout, no arrows */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-0 lg:hidden">
        {PILLARS.map((p, i) => {
          const Icon = ICON_MAP[p.icon];
          const isActive = selected === p.key;

          return (
            <button
              key={p.key}
              ref={(el) => { tabRefs.current[i] = el; }}
              role="tab"
              id={`stack-tab-${p.key}`}
              aria-selected={isActive}
              aria-controls={`stack-panel-${p.key}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onSelect(p.key)}
              onKeyDown={handleKeyDown}
              className={`group flex flex-col items-center text-center px-2 py-4 border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3832e3] ${
                isActive
                  ? 'border-[#3832e3] bg-[#3832e3]/5'
                  : 'border-zinc-300 hover:border-[#4D4D4D]'
              } ${i > 0 ? '-ml-px' : ''} ${i >= 2 ? '-mt-px sm:-mt-0' : ''} ${i >= 3 ? 'sm:-mt-px' : ''}`}
            >
              <span
                className={`font-mono text-[10px] uppercase tracking-[0.15em] mb-1 ${
                  isActive ? 'text-[#3832e3]' : 'text-zinc-400'
                }`}
              >
                {String(p.n).padStart(2, '0')}
              </span>
              {Icon && (
                <Icon
                  className={`w-5 h-5 mb-1 ${
                    isActive ? 'text-[#3832e3]' : 'text-zinc-500 group-hover:text-black'
                  }`}
                />
              )}
              <span
                className={`font-mono text-[10px] sm:text-xs uppercase tracking-[0.1em] font-medium leading-tight ${
                  isActive ? 'text-[#3832e3]' : 'text-black'
                }`}
              >
                {p.short}
              </span>
            </button>
          );
        })}
      </div>

      {/* Desktop: flex layout with chevron arrows between tabs */}
      <div className="hidden lg:flex items-stretch">
        {PILLARS.map((p, i) => {
          const Icon = ICON_MAP[p.icon];
          const isActive = selected === p.key;

          return (
            <React.Fragment key={p.key}>
              <button
                ref={(el) => { tabRefs.current[i] = el; }}
                role="tab"
                id={`stack-tab-${p.key}`}
                aria-selected={isActive}
                aria-controls={`stack-panel-${p.key}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => onSelect(p.key)}
                onKeyDown={handleKeyDown}
                className={`group flex-1 flex flex-col items-center text-center px-2 py-4 border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3832e3] ${
                  isActive
                    ? 'border-[#3832e3] bg-[#3832e3]/5'
                    : 'border-zinc-300 hover:border-[#4D4D4D]'
                } ${i > 0 ? '-ml-px' : ''}`}
              >
                <span
                  className={`font-mono text-[10px] uppercase tracking-[0.15em] mb-1 ${
                    isActive ? 'text-[#3832e3]' : 'text-zinc-400'
                  }`}
                >
                  {String(p.n).padStart(2, '0')}
                </span>
                {Icon && (
                  <Icon
                    className={`w-5 h-5 mb-1 ${
                      isActive ? 'text-[#3832e3]' : 'text-zinc-500 group-hover:text-black'
                    }`}
                  />
                )}
                <span
                  className={`font-mono text-[10px] sm:text-xs uppercase tracking-[0.1em] font-medium leading-tight ${
                    isActive ? 'text-[#3832e3]' : 'text-black'
                  }`}
                >
                  {p.short}
                </span>
                <span className="text-[9px] mt-0.5 leading-tight font-sans hidden xl:block text-zinc-500">
                  {p.label}
                </span>
              </button>
              {i < PILLARS.length - 1 && (
                <div className="flex items-center px-0.5" aria-hidden="true">
                  <ChevronRight className="w-4 h-4 text-zinc-400" />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default StackSelector;
